import React, { useState, useEffect } from "react";
import { Bookmark, Share2, Copy, Facebook, Twitter, MessageCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import placeholder from "../../assets/Car Image.png"; // Update with actual placeholder image
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import api from "../../lib/api.ts";

const CarHeader = ({ car, auctionLane }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);

  console.log("auction  car data " , car);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareDropdown && !event.target.closest('.share-dropdown-container')) {
        setShowShareDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareDropdown]);

  // Fetch wishlist on component mount to check if current vehicle is bookmarked
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id || !car?.id) return;

      try {
        const response = await api.get("/wishlist");
        const wishlistData = response.data?.data || response.data;

        if (wishlistData && Array.isArray(wishlistData)) {
          // Check if current vehicle is in wishlist
          const isInWishlist = wishlistData.some(item => item.vehicleId === car.id);
          setIsBookmarked(isInWishlist);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    fetchWishlist();
  }, [user?.id, car?.id]);

  // Toggle wishlist item
  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      // Redirect to login if not authenticated
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        // Use the action from API response to determine the new state
        const action = response.data.data?.action;
        const isAdded = action === "added";
        setIsBookmarked(isAdded);

        // Show appropriate toast message based on API response
        if (isAdded) {
          showSuccess("Vehicle added to Watch List successfully!");
        } else {
          showSuccess("Vehicle removed from Watch List successfully!");
        }
      } else {
        // Show the specific API error message
        const errorMessage = response.data.message || "Failed to update Watch List. Please try again.";
        showError(errorMessage);
      }
    } catch (error) {
      console.error("Error toggling Watch List:", error);
      // Show specific error message from API response if available
      const errorMessage = error.response?.data?.message || "Failed to update Watch List. Please try again.";
      showError(errorMessage);
    }
  };

  // Share functionality
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    if (car?.vehicle?.slug) {
      return `${baseUrl}/${car.vehicle.slug}`;
    }
    return window.location.href;
  };

  const getShareText = () => {
    return `Check out this amazing vehicle: ${car?.name} on Sky Auto  Auctions!`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      showSuccess("Link copied to clipboard!");
      setShowShareDropdown(false);
    } catch (error) {
      console.error("Failed to copy:", error);
      showError("Failed to copy link. Please try again.");
    }
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, '_blank', 'width=600,height=400');
    setShowShareDropdown(false);
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
    setShowShareDropdown(false);
  };

  const shareOnWhatsApp = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    setShowShareDropdown(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: car?.name,
          text: getShareText(),
          url: getShareUrl(),
        });
        setShowShareDropdown(false);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          showError("Failed to share. Please try again.");
        }
      }
    } else {
      // Fallback to copy to clipboard if native share is not supported
      copyToClipboard();
    }
  };

  function getNextCar(cars, currentCarId) {
    // Find current car - try different possible structures
    const currentCar = cars.find((c) => {
      const vehicleId = c.vehicle?.id || c.id;
      return vehicleId == currentCarId;
    });

    if (!currentCar) {
      return null;
    }

    // Get current run number
    const currentRunNo = currentCar.runNo;

    // Find the next car with runNo + 1
    const nextCar = cars.find((c) => c.runNo == currentRunNo + 1);
    return nextCar || null;
  }

  // Try different possible car ID structures and data sources
  const currentCarId = car?.id;
  const auctionVehicles =
    auctionLane?.auctionLaneVehicles || car?.auctionLanesVehicles || [];
  const nextCar = getNextCar(auctionVehicles, currentCarId);

  return (
    <div className="bg-[#1e293b] text-white py-5 px-8 rounded-xl shadow-lg border border-gray-700/50">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        {/* Left Side - Vehicle Info */}
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
            {car?.name}
          </h1>
          <div className="flex items-center gap-4 text-sm md:text-base mt-2">
            <div className="flex items-center gap-2 bg-black ml-2  text-white px-3 py-0.5 rounded text-[10px] sm:text-xs font-bold uppercase tracking-wider shadow-sm">
              <span className="text-white font-medium">Run No :</span>
              <span className="text-white font-semibold tracking-wide">{car?.runNo || "S2XXXXX"}</span>
            </div>
            <div className="text-gray-600">•</div>
             
            <div className="flex items-center gap-2">
              <span className="text-gray-400 font-medium">Lot :</span>
              <span className="text-gray-300 font-semibold tracking-wide">{car?.lotNumber || "S2XXXXX"}</span>
            </div>
 
            <div className="text-gray-600">•</div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 font-medium">Location:</span>
              <span className="text-gray-300 font-semibold">
                {car?.location?.name || car?.warehouse?.name || "Sharjah"}
              </span>
            </div>
            <div className="ml-2 bg-blue-600 text-white px-3 py-0.5 rounded text-[10px] sm:text-xs font-bold tracking-wider shadow-sm">
              Ending Soon
            </div>
          </div>
        </div>

        {/* Right Side - Share and Wishlist Icons */}
        <div className="flex items-center gap-3 share-dropdown-container relative">

          {/* Share Button */}
          <button
            className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            onClick={() => setShowShareDropdown(!showShareDropdown)}
          >
            <Share2 size={20} color="white" />
          </button>

          {/* Wishlist Button */}
          <button
            className={`p-2 rounded-full transition-colors ${isBookmarked
              ? 'bg-primary hover:bg-primary-dark'
              : 'bg-white/10 hover:bg-white/20'
              }`}
            onClick={() => toggleWishlist(car?.id)}
          >
            <Bookmark size={20} fill={isBookmarked ? "white" : "none"} stroke="white" />
          </button>

          {/* Share Dropdown */}
          {showShareDropdown && (
            <div
              className={`absolute top-full right-0 mt-2 w-48 rounded-lg shadow-lg bg-card border border-default z-50`}
            >
              {navigator.share && (
                <button
                  onClick={handleNativeShare}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
                >
                  <Share2 size={16} className="text-white" />
                  <span className="text-description">Share</span>
                </button>
              )}

              <button
                onClick={copyToClipboard}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
              >
                <Copy size={16} className="text-white" />
                <span className="text-description">Copy Link</span>
              </button>

              <button
                onClick={shareOnTwitter}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
              >
                <Twitter size={16} className="text-sky-400" />
                <span className="text-description">Twitter</span>
              </button>

              <button
                onClick={shareOnFacebook}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
              >
                <Facebook size={16} className="text-blue-600" />
                <span className="text-description">Facebook</span>
              </button>

              <button
                onClick={shareOnWhatsApp}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
              >
                <MessageCircle size={16} className="text-green-500" />
                <span className="text-description">WhatsApp</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarHeader;
